/**
 * Determines whether the given variable is empty.
 *
 * @param {*} variable - The variable to check if it is empty.
 * @returns {boolean} - True if the variable is empty, false otherwise.
 */
export const is_empty_var = ( variable ) => variable === '';

/**
 * Determines if the variable length exceeds the given length.
 *
 * @param {number} variableLength - The length of the variable to be checked.
 * @param {number} length - The threshold length to compare against.
 * @returns {boolean} - True if variable length exceeds the given length, false otherwise.
 */
export const max_length_var = ( variableLength, length ) => variableLength > length;

/**
 * Determines if the length of a variable is greater than or equal to the given length.
 *
 * @param {number} variableLength - The length of the variable to compare.
 * @param {number} length - The length to compare against.
 * @returns {boolean} True if the variable length is greater than or equal to the given length, otherwise false.
 */
export const max_equals_length_var = ( variableLength, length ) => variableLength >= length;

/**
 * Determines if the length of a variable is less than the specified length.
 *
 * @param {number} variableLength - The length of the variable.
 * @param {number} length - The desired length.
 * @returns {boolean} True if the length of the variable is less than the specified length, false otherwise.
 */
export const min_length_var = ( variableLength, length ) => variableLength < length;

/**
 * Determines if the length of a given variable is less than or equal to a specified length.
 *
 * @param {number} variableLength - The length of the variable.
 * @param {number} length - The specified length to compare against.
 * @returns {boolean} - True if the length of variable is less than or equal to the specified length, otherwise false.
 */
export const min_equals_length_var = ( variableLength, length ) => variableLength <= length;

/**
 * Determines whether the given variable value is greater than the specified value.
 *
 * @param {number} variableValue - The value of the variable to be compared.
 * @param {number} value - The value to compare against.
 * @returns {boolean} - True if variableValue is greater than value, false otherwise.
 */
export const max_value_var = ( variableValue, value ) => variableValue > value;

/**
 * Checks if the given variable value is equal to or greater than the specified value.
 *
 * @param {number} variableValue - The value of the variable to be compared.
 * @param {number} value - The value to compare against the variable value.
 * @returns {boolean} - `true` if the variable value is equal to or greater than the specified value,
 *                     `false` otherwise.
 */
export const max_equals_value_var = ( variableValue, value ) => variableValue >= value;

/**
 * Checks if the value of a given variable is less than a specified value.
 *
 * @param {number|string} variableValue - The value of the variable to compare.
 * @param {number|string} value - The value to compare against.
 * @returns {boolean} - Returns true if the variable value is less than the specified value, otherwise returns false.
 */
export const min_value_var = ( variableValue, value ) => variableValue < value;

/**
 * Checks if the given variable value is less than or equal to the specified value.
 *
 * @param {number|string} variableValue - The value of the variable to compare.
 * @param {number|string} value - The value to compare the variable value against.
 * @returns {boolean} - true if the variable value is less than or equal to the specified value, false otherwise.
 */
export const min_equals_value_var = ( variableValue, value ) => variableValue <= value;

/**
 * Checks if there are any errors in the given object.
 *
 * @param {Object} errors - The object to check for errors.
 * @returns {boolean} - A boolean value indicating if there are any errors.
 */
export const have_errors = ( errors ) => Object.keys( errors ).length === 0;

/**
 * Validates the GLN value.
 *
 * @param {string} value - The GLN value to validate.
 * @returns {boolean} - True if the GLN is valid, false otherwise.
 */
export const is_valid_gln = (value ) => {
    const regexGLN = /^\d{13}$/;
    return !is_empty_var( value ) && regexGLN.test( value );
};

/**
 * Checks if a given key is allowed or forbidden based on a predefined list.
 *
 * @param {string} key - The key to be checked.
 * @returns {boolean} - True if the key is allowed, false otherwise.
 */
export const on_check_key = ( key ) => {
    const keyForbidden = [ '_id', 'created_at', 'updated_at' ];
    return ( keyForbidden.indexOf( key ) < 0)
}

/**
 * Decode a JWT Token
 *
 * @param {string} token - Access Token
 * @returns {Object} decoded token
 */
export const decode_token = (token) => {
    return JSON.parse(window.atob(token.split('.')[1]))
};

/**
 * Checks if a given key is allowed or forbidden based on a predefined list.
 *
 * @param {string} token - Decoded Token
 * @returns {boolean} - True if the token its valid or false its expired
 */
export const is_token_valid = (token) => {
    const decoded_token = decode_token(token)
    return decoded_token.exp * 1000 > Date.now()
}
